import { useState, type ReactNode } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';

function isAdminConfirmTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('[data-admin-confirm]') != null;
}

type AdminFormDialogProps = {
    open: boolean;
    title: string;
    description?: ReactNode;
    children: ReactNode;
    error?: string | null;
    dirty?: boolean;
    saving?: boolean;
    saveLabel?: string;
    savingLabel?: string;
    saveDisabled?: boolean;
    maxWidthClass?: string;
    contentClassName?: string;
    bodyClassName?: string;
    onClose: () => void;
    onSave: () => void;
};

export function AdminFormDialog({
    open,
    title,
    description,
    children,
    error,
    dirty = false,
    saving = false,
    saveLabel = 'Guardar',
    savingLabel = 'Guardando…',
    saveDisabled = false,
    maxWidthClass = 'max-w-lg',
    contentClassName,
    bodyClassName,
    onClose,
    onSave,
}: AdminFormDialogProps) {
    const [discardOpen, setDiscardOpen] = useState(false);

    const requestClose = () => {
        if (dirty) {
            setDiscardOpen(true);
            return;
        }
        setDiscardOpen(false);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onOpenChange={(next) => {
                    if (!next) requestClose();
                }}
            >
                <DialogContent
                    aria-describedby={undefined}
                    className={cn(
                        'max-h-[92vh] overflow-y-auto bg-card p-5 sm:rounded-xl',
                        maxWidthClass,
                        contentClassName
                    )}
                    onEscapeKeyDown={(event) => {
                        if (discardOpen || dirty) event.preventDefault();
                        if (dirty && !discardOpen) setDiscardOpen(true);
                    }}
                    onPointerDownOutside={(event) => {
                        if (isAdminConfirmTarget(event.target)) {
                            event.preventDefault();
                            return;
                        }
                        if (discardOpen || dirty) event.preventDefault();
                        if (dirty && !discardOpen) setDiscardOpen(true);
                    }}
                    onInteractOutside={(event) => {
                        if (isAdminConfirmTarget(event.target)) {
                            event.preventDefault();
                            return;
                        }
                        if (discardOpen || dirty) event.preventDefault();
                    }}
                >
                    <DialogHeader className="shrink-0">
                        <DialogTitle>{title}</DialogTitle>
                        {description != null && description !== '' && (
                            <div className="text-sm text-muted-foreground">{description}</div>
                        )}
                    </DialogHeader>
                    <div className={cn('space-y-3', bodyClassName)}>{children}</div>
                    {error ? (
                        <div
                            role="alert"
                            className="shrink-0 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
                        >
                            {error}
                        </div>
                    ) : null}
                    <DialogFooter className="shrink-0 gap-2 sm:space-x-0">
                        <button
                            type="button"
                            className="px-3 py-2 text-sm"
                            onClick={requestClose}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="px-3 py-2 text-sm rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                            disabled={saving || saveDisabled}
                            onClick={onSave}
                        >
                            {saving ? savingLabel : saveLabel}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={open && discardOpen}
                onOpenChange={(next) => {
                    if (!next) setDiscardOpen(false);
                }}
            >
                <DialogContent
                    aria-describedby="admin-form-discard-desc"
                    className="z-[110] max-w-md bg-card p-0 sm:rounded-2xl gap-0"
                    onPointerDownOutside={(event) => event.stopPropagation()}
                    onInteractOutside={(event) => event.stopPropagation()}
                >
                    <DialogHeader className="flex flex-row items-start gap-3 space-y-0 p-5 border-b border-border/50 text-left">
                        <div className="p-2 rounded-xl shrink-0 bg-rose-500/10">
                            <AlertTriangle size={18} className="text-rose-400" />
                        </div>
                        <div className="min-w-0 pt-0.5">
                            <DialogTitle>¿Descartar cambios?</DialogTitle>
                            <p id="admin-form-discard-desc" className="mt-2 text-sm text-muted-foreground">
                                Hay cambios sin guardar. Si sales ahora se pierden.
                            </p>
                        </div>
                    </DialogHeader>
                    <DialogFooter className="p-5 gap-2 sm:space-x-0">
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                            onClick={() => setDiscardOpen(false)}
                        >
                            Seguir editando
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border border-rose-500/25"
                            onClick={() => {
                                setDiscardOpen(false);
                                onClose();
                            }}
                        >
                            Descartar
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
